import { useState, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  Button,
  Chip,
  FormHelperText,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { BugPriority, BugReport } from "../../store/slice/bug-fix/types";

import { useAppSelector } from "../../store/hooks";

type AssignmentPanelProps = {
  bug?: BugReport | null;
  assigning?: boolean;
  onAssign: (payload: {
    bug: BugReport;
    developerId: number;
    priority: BugPriority;
  }) => Promise<void> | void;
};

const priorities: { label: string; value: BugPriority }[] = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
  { label: "Urgent", value: "urgent" },
];

const AssignmentPanel = ({
  bug,
  assigning = false,
  onAssign,
}: AssignmentPanelProps) => {
  const [developerId, setDeveloperId] = useState<string>("");
  const [priority, setPriority] = useState<BugPriority>("medium");
  const [error, setError] = useState<string | null>(null);

  const { users } = useAppSelector((state) => state.users);
  const developers = useMemo(() => {
    return users.filter(user => user.role.toLocaleLowerCase() === "dev")
  }, [users])
  const handleSubmit = async () => {
    if (!bug) return;
    if (!developerId) {
      setError("Please select a developer.");
      return;
    }
    setError(null);
    await onAssign({
      bug,
      developerId: Number(developerId),
      priority,
    });
  };
  return (
    <Box
      sx={{
        backgroundColor: "rgba(219, 234, 254, 1)", // light blue
        borderRadius: "16px",
        p: 2.5,
        width: "100%",
        my: 3
      }}
    >
      <Stack spacing={2}>
        {/* Assigned Developer */}
        <Box>
          <Typography
            variant="caption"
            fontWeight={600}
            letterSpacing={0.5}
            mb={0.5}
          >
            ASSIGNED DEVELOPER
          </Typography>

          <Select
            fullWidth
            size="small"
            displayEmpty
            value={developerId}
            onChange={(event) => {
              setDeveloperId(String(event.target.value));
              setError(null);
            }}
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "10px",
            }}

          >
            <MenuItem value="">
              <em>Select developer here</em>
            </MenuItem>
            {developers.map((dev) => {
              return (
                <MenuItem key={dev.id} value={dev.id}>
                  {dev.first_name} {" "} {dev.last_name}
                </MenuItem>
              );
            })}

            {/* <MenuItem value={116}>Camaraderie Mavenga</MenuItem>
            <MenuItem value={117}>Nigel Tavaguta</MenuItem>
            <MenuItem value={118}>Nashel Mashumba</MenuItem>
            <MenuItem value={93}>Dancel Mautsa</MenuItem>
            <MenuItem value={88}>Stanford Mendamenda</MenuItem>
            <MenuItem value={9}>Leon Manimhazi</MenuItem>
            <MenuItem value={17}>Hurtman Sibanda</MenuItem> */}

          </Select>
          {error && (
            <FormHelperText sx={{ color: "error.main", mt: 0.5 }}>
              {error}
            </FormHelperText>
          )}
        </Box>

        {/* Priority */}
        <Box gap={3}>
          <Typography
            sx={{
              marginBottom: 2
            }}
          >
            What&apos;s the priority for this task?
          </Typography>

          <Stack direction="row" spacing={1}>
            {priorities.map((priorityOption) => (
              <Chip
                key={priorityOption.value}
                label={priorityOption.label}
                clickable
                color={priority === priorityOption.value ? "primary" : undefined}
                onClick={() => setPriority(priorityOption.value)}
                sx={{
                  backgroundColor: priority === priorityOption.value
                    ? "rgba(120, 72, 55, 1)"
                    : "#fff",
                  borderRadius: "999px",
                  px: 1.5,
                  fontWeight: 500,
                  border:
                    priority === priorityOption.value
                      ? "1px solid rgba(120, 72, 55, 1)"
                      : "1px solid transparent",
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={2} mt={1}>
          {/* <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "rgba(241, 245, 249, 1)",
              color: "rgba(51, 65, 85, 1)",
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(226, 232, 240, 1)",
                boxShadow: "none",
              },
            }}
          >
            Send a reminder
          </Button> */}

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={!bug || assigning}
            sx={{
              backgroundColor: "rgba(120, 72, 55, 1)", // brown
              color: "#fff",
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(99, 60, 46, 1)",
                boxShadow: "none",
              },
            }}
          >
            {assigning ? "Assigning..." : "Assign Developer"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default AssignmentPanel;
